// Command Center API client: same-origin fetches against the FastAPI backend.
// Every failure surfaces a plain-English message — never a status code on the face of the board.
const JSON_HEADERS = { "Content-Type": "application/json" };

async function call(path, opts = {}) {
  let resp;
  try {
    resp = await fetch(path, { headers: JSON_HEADERS, ...opts });
  } catch {
    const e = new Error("The board can't reach its actions right now.");
    e.kind = "down";
    throw e;
  }
  let body = {};
  try {
    body = await resp.json();
  } catch {
    body = {};
  }
  if (!resp.ok) {
    const e = new Error(
      body.detail || "That didn't go through — try again or tell Kit.",
    );
    e.kind = resp.status === 401 ? "auth" : "rejected";
    e.status = resp.status;
    throw e;
  }
  return body;
}

export const session = () => call("/api/session");
export const login = (pin) =>
  call("/api/login", { method: "POST", body: JSON.stringify({ pin }) });
export const logout = () => call("/api/logout", { method: "POST" });
export const fetchBoard = (name) => call(`/api/board/${name}`);
export const act = ({
  verb,
  board,
  target,
  note,
  value, // rename: new title · editText: replacement text
  move, // reorder: -1 up · +1 down
  craft, // promoteIdea: {goal, phases[], definition_of_done, skills[], resources[]}
}) =>
  call("/api/action", {
    method: "POST",
    body: JSON.stringify({
      verb,
      board,
      target,
      ...(note !== undefined ? { note } : {}),
      ...(value !== undefined ? { value } : {}),
      ...(move !== undefined ? { move } : {}),
      ...(craft !== undefined ? { craft } : {}),
    }),
  });
export const addWork = (title, why, attachIds) =>
  call("/api/add-work", {
    method: "POST",
    body: JSON.stringify({
      title,
      why,
      ...(attachIds?.length ? { attach_ids: attachIds } : {}),
    }),
  });
// ── Phase 9: run a work card on the Studio (dispatch bridge) ──────────────
// The card's own words become the prompt; the backend picks the model from
// the worker inventory. A refusal comes back as a 409 with detail — surfaced
// verbatim by the caller's flash.
export const dispatchFromCard = (slug, board = "work") =>
  call("/api/exec/dispatch-from-card", {
    method: "POST",
    body: JSON.stringify({ slug, board }),
  });
// ── mail engine (slice 1: search + full bodies + real reply) ──────────────
export const fetchMailBody = (key) =>
  call(`/api/kitmail/body/${encodeURIComponent(key)}`);
export const searchMail = (q) =>
  call(`/api/kitmail/search?q=${encodeURIComponent(q)}`);
export const queueMailReply = (target, body, subject) =>
  call("/api/kitmail/reply", {
    method: "POST",
    body: JSON.stringify({ target, body, ...(subject ? { subject } : {}) }),
  });
// ── mail folders + sent (slice 2: browse past INBOX, read outbound) ───────
export const fetchMailFolders = (mailbox) =>
  call(
    `/api/kitmail/folders${mailbox ? `?mailbox=${encodeURIComponent(mailbox)}` : ""}`,
  );
export const fetchMailMessages = (mailbox, folder, limit, cursor) => {
  const q = new URLSearchParams({ mailbox, folder: folder || "INBOX" });
  if (limit) q.set("limit", String(limit));
  if (cursor) q.set("cursor", cursor);
  return call(`/api/kitmail/messages?${q.toString()}`);
};
export const fetchMailSent = (account, limit) => {
  const q = new URLSearchParams();
  if (account) q.set("account", account);
  if (limit) q.set("limit", String(limit));
  const s = q.toString();
  return call(`/api/kitmail/sent${s ? `?${s}` : ""}`);
};
export const fetchMailSentOne = (key) =>
  call(`/api/kitmail/sent/${encodeURIComponent(key)}`);
export const fetchMailAccounts = () => call("/api/kitmail/accounts");
export const fetchMailAccountsHealth = () =>
  call("/api/kitmail/accounts/health");
// ── docs (P1): the workspace reader ───────────────────────────────────────
// The reader is read-only in P1: it shows a document, its history, and a
// PDF. It never writes.
export const fetchDocs = ({ q, lane, brand, limit, offset } = {}) => {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (lane) params.set("lane", lane);
  if (brand) params.set("brand", brand);
  if (limit) params.set("limit", String(limit));
  if (offset) params.set("offset", String(offset));
  const s = params.toString();
  return call(`/api/docs${s ? `?${s}` : ""}`);
};
export const fetchDoc = (id) => call(`/api/docs/${encodeURIComponent(id)}`);
export const fetchDocHistory = (id, compare) => {
  const s = compare ? `?compare=${encodeURIComponent(compare)}` : "";
  return call(`/api/docs/${encodeURIComponent(id)}/history${s}`);
};
export const fetchDocIndex = () => call("/api/docs/index");
// ── docs (P3.2 Phase B): per-brand doc hubs ───────────────────────────────
// GET /api/docs/brands answers exactly [{name, count, pdfCount}] — sorted
// descending by count, ties by name (DOC-P32-CONTRACT.md). The list view
// renders one chip per row; empty [] means no chips.
export const fetchDocBrands = () => call("/api/docs/brands");
// ── docs (P3.1): backlinks — what links TO this doc ────────────────────────
// GET /api/docs/(id)/backlinks answers exactly {ok, docId, count, backlinks}
// per DOC-BACKLINKS-CONTRACT.md. An unknown document answers 404; the thrown
// error carries that .status so the reader can drop the panel entirely
// instead of apologizing for a doc it never had.
export const fetchDocBacklinks = (id) =>
  call(`/api/docs/${encodeURIComponent(id)}/backlinks`);
export const docPdfUrl = (id) => `/api/docs/${encodeURIComponent(id)}/pdf`;
// ── docs (P2): the editor ──────────────────────────────────────────────────
// An edit is a REQUEST, not a writeKit-side command: Save hands the draft to
// the backend with the base sha8 it was edited from, and the backend queues
// it to write to the Mac. The screen then polls /edits for that edit's life:
//   queued → applying → applied | conflict | failed | cancelled
// A conflict means the Mac's file moved under the draft; the draft is
// preserved on the screen either way.
export const postDocEdit = (id, body) =>
  call(`/api/docs/${encodeURIComponent(id)}/edit`, {
    method: "POST",
    body: JSON.stringify(body),
  });
export const fetchDocEdits = (id) =>
  call(`/api/docs/${encodeURIComponent(id)}/edits`);
export const postDocEditCancel = (editId) =>
  call(`/api/docs/edits/${encodeURIComponent(editId)}/cancel`, {
    method: "POST",
  });
// ── docs (P3.4): annotations — threaded notes, cloud store only ────────────
// Annotations are conversation ABOUT files (never files): they live in the
// backend store, do not ride doc-sync, and never touch the Mac. Shapes:
//   annotation = {id, doc_id, parent_id, state, author, body, page, rect,
//                 quote, quote_start, quote_context, stale, created_at,
//                 updated_at, resolved_at, resolved_by}
// GET list answers {annotations: [...]} newest-first with replies nested one
// level (each top-level row carries `replies`). POST rect outside 0..1 is a
// 422; body over 5,000 chars is a 413 — the caller surfaces the plain words.
export const fetchDocAnnotations = (docId) =>
  call(`/api/docs/${encodeURIComponent(docId)}/annotations`);
export const createDocAnnotation = (docId, payload) =>
  call(`/api/docs/${encodeURIComponent(docId)}/annotations`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
export const updateDocAnnotation = (id, patch) =>
  call(`/api/annotations/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
export const replyDocAnnotation = (id, body) =>
  call(`/api/annotations/${encodeURIComponent(id)}/reply`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });
export const fetchOpenAnnotations = () =>
  call("/api/docs/annotations/open");
// ── music (phase 2-3): the daily set as a board ───────────────────────────
// The catalog is a board like any other: /api/board/music when the store is
// live, the baked musicBoard.json when it is not. The AUDIO is a separate
// thing: the files live on Kit's machine, so the URL is built from the
// catalog's file reference and degrades to a 404 ("local only") until the
// backend mounts the renders dir.
export const fetchMusicBoard = () => call("/api/board/music");
export const musicAudioUrl = (day, file) =>
  `/api/music-audio/${encodeURIComponent(day || "")}/${encodeURIComponent(file || "")}`;
